import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { IconButton } from '@mui/material'
import { ArrowBack } from '@mui/icons-material'

import { formatContributionDescription, toAmountString, toDateStringNumeric } from '../../utils'
import { queries } from '../../graphql'
import { CardBox, Container, DataGrid, Icons } from '../../components'

// Companion to investment-portfolio-activity.jsx -- lists investment-wallet
// activity that ISN'T attributable to one specific Investment Account: a
// Contribution or Balance Adjustment whose portfolioId is null (pre-existing/
// legacy rows, a Contribution predating FLIGHT-299, or ones deliberately not
// tied to a specific account). Shown here once instead of being duplicated
// into every portfolio's own activity table.
export function InvestmentUnlinkedActivity() {
  const navigate = useNavigate()
  const fundId = Number(useParams().fundId)

  const { loading, data } = useQuery(queries.funds.myFundActivity, { variables: { fundId } })
  const fund = data?.me?.fund
  // This page IS the unlinked-activity card, so it's the donor's only card
  // whenever there are no real Investment Accounts to also show one for --
  // matching the index page's redirect condition (see investment-account.jsx).
  // In that case there's nothing to go Back to (Back would just land on the
  // index, which immediately redirects right back here), so no arrow.
  const isOnlyCard = (fund?.investmentPortfolios || []).length === 0

  const rows = useMemo(() => {
    const contributionRows = (fund?.contributions || [])
      .filter((c) => c.walletType === 'investment' && !c.portfolioId)
      .map((c) => ({
        id: `c${c.id}`,
        __typename: 'Contribution',
        date: c.receiptedDate,
        type: 'Contribution',
        description: formatContributionDescription(c),
        amount: c.amount,
      }))

    const balanceAdjustmentRows = (fund?.balanceAdjustments || [])
      // A transfer-linked row always carries a portfolioId on its
      // investment leg (see investment-transfer.flow.ts), so this filter is
      // defensive/for consistency rather than expected to change behavior
      // here -- it shouldn't be possible for such a row to also be
      // portfolioId-less.
      .filter((ba) => ba.walletType === 'investment' && !ba.portfolioId && !ba.investmentTransferId)
      .map((ba) => ({
        id: `ba${ba.id}`,
        __typename: 'BalanceAdjustment',
        date: ba.createdAt,
        type: 'Balance Adjustment',
        description: ba.description || 'Balance Adjustment',
        amount: ba.amount,
      }))

    return [...contributionRows, ...balanceAdjustmentRows]
  }, [fund])

  const columns = [
    {
      field: '__typename',
      headerName: 'Transaction',
      pinnable: true,
      hideable: true,
      flex: 0.5,
      align: 'center',
      minWidth: 50,
      renderCell: ({ row } = {}) => <Icons.ActivityItem row={row} />,
      renderHeader: () => null,
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 130,
      valueFormatter: ({ value } = {}) => toDateStringNumeric(value),
    },
    { field: 'type', headerName: 'Type', flex: 1.5, minWidth: 220 },
    { field: 'description', headerName: 'Description', flex: 3, minWidth: 320 },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      minWidth: 130,
      valueFormatter: ({ value } = {}) => toAmountString(value),
    },
  ]

  // Back arrow renders inline with the export button via DataGrid's own
  // title slot, same pattern as investment-portfolio-activity.jsx -- omitted
  // entirely (not just the arrow) when this is the donor's only card, since
  // there'd be nothing left in the title slot without it (see isOnlyCard
  // above).
  const title = isOnlyCard ? null : (
    <IconButton size="small" onClick={() => navigate('..')}>
      <ArrowBack />
    </IconButton>
  )

  return (
    <Container maxWidth="lg">
      <CardBox>
        <DataGrid
          rows={rows}
          columns={columns}
          title={title}
          autoRowHeight
          loading={loading}
          exportFileName="Other Investment Wallet Activity"
          initialState={{ sorting: { sortModel: [{ field: 'date', sort: 'desc' }] } }}
        />
      </CardBox>
    </Container>
  )
}
